/*
  Responsible for loading and formatting the account/publication data
  to be used by the AccountSwitcher component.
*/

import { withCookieJar } from '../../utils/cookie_jar';

export class Account {
  switcherLoader:AccountSwitcherLoader;
  name:string;
  id:number;
  children:any[] = [];

  constructor(switcherLoader:AccountSwitcherLoader, data:any) {
    this.switcherLoader = switcherLoader;
    this.name = data.name;
    this.id = parseInt(data.id, 0);
  }

  get publications() {
    return this.switcherLoader.publications.filter(
      (publication:Publication) => publication.accountId === this.id
    );
  }

  serialize() {
    return {
      name: this.name,
      id: this.id,
      children: this.publications.map((publication:Publication) => publication.serialize())
    };
  }
}

export class Publication {
  switcherLoader:AccountSwitcherLoader;
  name:string;
  id:number;
  accountId:number;

  constructor(switcherLoader:AccountSwitcherLoader, data:any) {
    this.switcherLoader = switcherLoader;
    this.name = data.name;
    this.id = parseInt(data.id, 0);
    this.accountId = parseInt(data.accountId, 0);
  }

  get account() {
    return this.switcherLoader.accounts.find((account:Account) => account.id === this.accountId);
  }

  serialize() {
    return {
      name: this.name,
      id: this.id,
      accountId: this.accountId
    };
  }
}

// This class must be re-initialized whenever permission data or the current page changes
// The publications async permission checks are cached so serailization is synchronous and fast
class AccountSwitcherLoader {
  publications:Publication[];
  accounts:Account[];
  activeAccountId = '';
  activePublicationId = '';

  // Matches campaigns and media lib
  static ACCOUNT_ID_MATCHER = /(\/organizations\/)(\d*)|(account_id=)(\d*)/;

  // Matches pub settings, media lib, and analytics
  static PUBLICATION_ID_MATCHER = /(\/publications\/)(\d*)|(brand_profile_id=)(\d*)|(publication_id=)(\d*)|(\/analytics\/)(\d*)/;

  // Matches id of nested resource, and also the edit path
  // i.e. organizations/:org_id/campaigns/:id or publications/:pub_id/teams/:id/edit
  static RESOURCE_ID_MATCHER = /\/\d+$|\/\d+\/edit$/;

  static accountIdFromLocation(location = null) {
    const { pathname, search } = location || window.location;
    const match = (pathname + search).match(this.ACCOUNT_ID_MATCHER) || [];
    // For each possible match group
    return match[2] || match[4];
  }

  static publicationIdFromLocation(location = null) {
    const { pathname, search } = location || window.location;
    const match = (pathname + search).match(this.PUBLICATION_ID_MATCHER) || [];
    // For each possible match group
    return match[2] || match[4] || match[6] || match[8];
  }

  static handleAccountScopedPage(accountId:number) {
    const changeLocation = this.accountIdFromLocation();
    if (changeLocation) {
      // Assuming that capture groups 1 or 3 have been matched before the id
      let newLocation = window.location.href.replace(this.ACCOUNT_ID_MATCHER, `$1$3${accountId}`);
      // HACK: remove `/:id` when switching accounts on show pages
      newLocation = newLocation.replace(this.RESOURCE_ID_MATCHER, '');

      window.location.href = newLocation;
    }
    return changeLocation;
  }

  static handlePublicationScopedPage(publicationId:number) {
    const changeLocation = this.publicationIdFromLocation();
    if (changeLocation) {
      // Assuming that capture groups 1, 3, 5, 7 have been matched before the id
      let newLocation = window.location.href.replace(
        this.PUBLICATION_ID_MATCHER,
        `$1$3$5$7${publicationId}`
      );
      // HACK: remove s_ids search param from analytics urls when switching pubs
      newLocation = this.removeURLParameter(newLocation, 's_id');
      // HACK: remove `/:id` or `/:id/edit` when switching pubs on edit/show pages
      newLocation = newLocation.replace(this.RESOURCE_ID_MATCHER, '');

      window.location.href = newLocation;
    }

    return changeLocation;
  }

  static removeURLParameter(url:string, paramTarget:any) {
    const urlParts = url.split('?');
    if (urlParts.length === 1) return url;

    const prefix = `${window.encodeURIComponent(paramTarget)}=`;
    const params = urlParts[1].split(/[&;]/g);

    return `${urlParts[0]}?${params.filter(p => !p.startsWith(prefix))}`;
  }

  constructor(userData:any) {
    this.publications = userData.brandProfiles.map((bp:any) => new Publication(this, bp));
    this.accounts = userData.accounts.map((account:Account) => new Account(this, account));
    this.loadActiveData();
  }

  get activeAccount() {
    return this.accounts.find(account => account.id === parseInt(this.activeAccountId, 0));
  }

  set activeAccount({ id }:any) {
    this.activeAccountId = id;
  }

  get activePublication() {
    return this.publications.find(
      publication => publication.id === parseInt(this.activePublicationId, 0)
    );
  }

  set activePublication({ id }:any) {
    this.activePublicationId = id;
  }

  loadActiveData() {
    // Bail out cleanly if the user has no staffed data — nav.tsx guards for
    // null active* and keeps the loading placeholder rather than crashing.
    if (this.accounts.length === 0 || this.publications.length === 0) return;

    let activeAccount = this.activeAccount || this.accounts[0];
    let activePublication =
      this.activePublication || activeAccount.publications[0] || this.publications[0];

    const currentAccountId = parseInt(AccountSwitcherLoader.accountIdFromLocation(), 0);

    if (currentAccountId) {
      const matchedAccount = this.accounts.find(account => account.id === currentAccountId);
      // If the URL points to an account the user isn't staffed on, keep the
      // existing activeAccount rather than crashing on undefined.id below.
      if (matchedAccount) {
        activeAccount = matchedAccount;
        activePublication =
          activeAccount.id === activePublication.accountId
            ? activePublication
            : activeAccount.publications[0] || activePublication;
      }
    }

    const currentPubId = parseInt(AccountSwitcherLoader.publicationIdFromLocation(), 0);

    if (currentPubId) {
      const matchedPublication = this.publications.find(
        publication => publication.id === currentPubId
      );
      // Same guard: an unstaffed publication_id in the URL (stale bookmark,
      // cross-env link, revoked access) must not NPE on .account.
      if (matchedPublication) {
        activePublication = matchedPublication;
        activeAccount = matchedPublication.account || activeAccount;
      }
    }

    this.activeAccount = activeAccount;
    this.activePublication = activePublication;
  }

  serialize() {
    return this.accounts.map(account => account.serialize());
  }
}

export default withCookieJar(AccountSwitcherLoader, 'activeAccountId', 'activePublicationId');
